import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { OverviewCardComponent } from '../../../../../shared/charts/components/elements/overview-card/overview-card.component';
import { LineChartComponent } from '../../../../../shared/charts/components/elements/line-chart/line-chart.component';
import { TablePaginationComponent } from '../../../../../shared/components/table-pagination/table-pagination.component';
import { DateRangePickerInputComponent, DateRange } from '../../../../../shared/date-picker';
import { DashboardApiService } from '../../../services/dashboard-api.service';
import { DeliveryDashboardStats } from '../../../../../shared/models/delivery-dashboard-stats';
import { TopDriver } from '../../../../../shared/models/top-driver';
import { UnassignedOrder } from '../../../../../shared/models/unassigned-order';
import { DeliveryPerformance } from '../../../../../shared/models/delivery-performance';
import { SalesSeries, SalesGranularity } from '../../../../../shared/models/sales-point';
import { GenericPaginationResponseBody } from '../../../../../shared/models/Generic-pagination-response-body';
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { LanguageService } from '../../../../../shared/services/translations/language.service';

@Component({
  selector: 'app-delivery-dashboard',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterLink, TranslateModule,
    OverviewCardComponent, LineChartComponent,
    TablePaginationComponent, DateRangePickerInputComponent,
  ],
  templateUrl: './delivery-dashboard.component.html',
  styleUrl: './delivery-dashboard.component.css'
})
export class DeliveryDashboardComponent implements OnInit {

  apiService = inject(DashboardApiService);
  toast = inject(ToastNotificationService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  loaded = false;

  stats: DeliveryDashboardStats | undefined;
  performance: DeliveryPerformance | undefined;

  seriesRange: DateRange | null = null;
  deliveriesLabels: string[] = [];
  deliveriesData: any[] = [];

  topDrivers: TopDriver[] = [];

  unassignedResponse: GenericPaginationResponseBody<UnassignedOrder> | undefined;
  unassignedPagination: LimitOffsetPagination = { limit: 5, offset: 0 };

  ngOnInit(): void {
    this.seriesRange = this.thisWeekRange();

    forkJoin({
      stats: this.apiService.getDeliveryDashboardStats(),
      performance: this.apiService.getDeliveryPerformance(),
      series: this.apiService.getDeliveryDeliveriesSeries(this.seriesRange),
      drivers: this.apiService.getDeliveryTopDrivers(),
      unassigned: this.apiService.getDeliveryUnassignedOrders(this.unassignedPagination),
    }).subscribe({
      next: ({ stats, performance, series, drivers, unassigned }) => {
        this.stats = stats;
        this.performance = performance;
        this.applySeries(series);
        this.topDrivers = drivers;
        this.unassignedResponse = unassigned;
        this.loaded = true;
      },
      error: () => {
        this.loaded = true;
        this.toast.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_CENTER);
      }
    });
  }

  abs(n: number): number {
    return Math.abs(n);
  }

  formatDuration(minutes: number | null): string {
    if (minutes == null) return '—';
    const total = Math.round(minutes);
    if (total < 60) {
      return `${total} ${this.translate.instant('dashboard.delivery.performance.unit_min')}`;
    }
    const h = Math.floor(total / 60);
    const m = total % 60;
    const hPart = `${h}${this.translate.instant('dashboard.delivery.performance.unit_h')}`;
    return m ? `${hPart} ${m}${this.translate.instant('dashboard.delivery.performance.unit_m')}` : hPart;
  }

  weekdayName(jsWeekday: number): string {
    // 2024-01-07 is a Sunday (getDay() === 0); offset to the requested weekday.
    const date = new Date(2024, 0, 7 + jsWeekday);
    return date.toLocaleDateString(this.languageService.currentLang(), { weekday: 'short' });
  }

  applySeries(series: SalesSeries): void {
    this.deliveriesLabels = series.points.map(p => this.formatSeriesLabel(p.date, series.granularity));
    this.deliveriesData = this.buildDeliveriesDataset(series.points.map(p => p.total));
  }

  onSeriesRangeChange(range: DateRange | null): void {
    this.seriesRange = range;
    this.apiService.getDeliveryDeliveriesSeries(range).subscribe({
      next: series => this.applySeries(series),
      error: () => {
        this.toast.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_CENTER);
      }
    });
  }

  updateUnassignedPagination(pagination: LimitOffsetPagination): void {
    this.unassignedPagination = pagination;
    this.apiService.getDeliveryUnassignedOrders(pagination).subscribe({
      next: res => { this.unassignedResponse = res; },
      error: () => {
        this.toast.createNotification(this.translate.instant('dashboard.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('dashboard.errors.load_failed_title'), ToastPosition.TOP_CENTER);
      }
    });
  }

  private buildDeliveriesDataset(data: number[]): any[] {
    return [
      {
        label: this.translate.instant('dashboard.delivery.charts.deliveries'),
        data,
        borderColor: '#16BDCA',
        borderWidth: 2,
        pointRadius: 2.5,
        pointHoverRadius: 5,
        pointBackgroundColor: '#16BDCA',
        pointBorderColor: '#16BDCA',
        pointHoverBackgroundColor: '#16BDCA',
        pointHoverBorderColor: '#16BDCA',
        pointBorderWidth: 1.5,
        tension: 0.4,
      },
    ];
  }

  private formatSeriesLabel(iso: string, granularity: SalesGranularity): string {
    const [year, month, day] = iso.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const lang = this.languageService.currentLang();
    switch (granularity) {
      case 'year':
        return date.toLocaleDateString(lang, { year: 'numeric' });
      case 'month':
        return date.toLocaleDateString(lang, { month: 'short', year: '2-digit' });
      default:
        return date.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
    }
  }

  thisWeekRange(): DateRange {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekday = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (weekday === 0 ? 6 : weekday - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: this.toIsoDate(monday), end: this.toIsoDate(sunday) };
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
